// app/src/lib/procurement/threeWayMatch.js — PO ↔ delivery slip ↔ invoice (US-2.13, US-2.14).
//
// Variance types (per spec):
//   - short:         delivered < ordered.
//   - over:          delivered > ordered.
//   - price_change:  invoice unit price differs from PO unit price.
//   - substitution:  delivered description differs from ordered description.
//
// A `MatchResult` is attached to the Invoice (`matchResult` field). The result
// is recomputed every time a new piece of the triple lands (PO, slip, invoice),
// so we never have stale matches.

import { db } from '../db.js';
import { requireUserId, similarity, isoToday } from './util.js';

const PRICE_CHANGE_TOLERANCE_CENTS = 1; // $0.01
const DESCRIPTION_SIMILARITY_FLOOR = 0.85; // below = substitution.

/**
 * @typedef {Object} Variance
 * @property {"short"|"over"|"price_change"|"substitution"} type
 * @property {string} itemId
 * @property {string} description
 * @property {number=} qtyOrdered
 * @property {number=} qtyDelivered
 * @property {number=} qtyInvoiced
 * @property {number=} poUnitPriceCents
 * @property {number=} invoiceUnitPriceCents
 * @property {string=} substitutedDescription
 * @property {"open"|"approved"|"disputed"|"adjusted"} resolution
 */

/** @typedef {{ matchedAt:number, poId?:string, slipId?:string, invoiceId:string, variances:Variance[] }} MatchResult */

/**
 * Capture a delivery slip. Called from the OCR confirm flow when document type
 * is "delivery slip". Tries to auto-link to an open PO by matching PO number
 * extracted from the slip image (best-effort — caller can pass `poNumber`).
 *
 * @param {{ vendorId:string, deliveryDate?:string, slipNumber?:string, poNumber?:string, expectedDeliveryDate?:string, lines:Array<{ itemId?:string, description:string, quantityOrdered?:number, quantityDelivered:number, uom:string }>, s3Key?:string }} input
 */
export async function captureSlip(input) {
  const userId = await requireUserId();
  if (!input.vendorId) throw new Error('slip.capture: vendorId required');

  const slipId = crypto.randomUUID();
  /** Look up open PO if poNumber present. */
  let linkedPoId;
  if (input.poNumber) {
    const openPOs = await db.purchaseOrders
      .where('vendorId').equals(input.vendorId)
      .filter((p) => p.userId === userId && (p.status === 'SUBMITTED' || p.status === 'PARTIAL') && (p.notes || '').includes(input.poNumber))
      .first();
    if (openPOs) linkedPoId = openPOs.poId;
  }

  const slip = {
    slipId,
    userId,
    vendorId: input.vendorId,
    invoiceId: undefined,
    deliveryDate: input.deliveryDate || isoToday(),
    expectedDeliveryDate: input.expectedDeliveryDate,
    status: 'PROCESSED',
    slipNumber: input.slipNumber,
    poId: linkedPoId,
    s3Key: input.s3Key,
  };
  const lines = input.lines.map((l) => ({
    slipLineId: crypto.randomUUID(),
    userId,
    slipId,
    invoiceLineId: undefined,
    itemId: l.itemId,
    description: l.description,
    quantityOrdered: Number(l.quantityOrdered) || undefined,
    quantityDelivered: Number(l.quantityDelivered) || 0,
    uom: l.uom || 'each',
  }));

  await db.transaction('rw', db.deliverySlips, db.deliveryLineItems, async () => {
    await db.deliverySlips.put(slip);
    if (lines.length) await db.deliveryLineItems.bulkPut(lines);
  });

  // If a PO is linked, re-run the match.
  if (linkedPoId) await tryMatchByPo(linkedPoId);
  return { slip, lines };
}

/** Try to fully match a triple given a PO id (called when slip OR invoice arrives). */
export async function tryMatchByPo(poId) {
  const userId = await requireUserId();
  const po = await db.purchaseOrders.get(poId);
  if (!po || po.userId !== userId) return null;

  // Find an invoice referencing this PO number/id.
  const invoice = await db.invoices
    .where('vendorId').equals(po.vendorId)
    .filter((i) => i.userId === userId && (i.poNumber === po.poId || i.poNumber === po.notes || i.poId === po.poId))
    .first();
  // Find a slip linked to this PO.
  const slip = await db.deliverySlips
    .where('vendorId').equals(po.vendorId)
    .filter((s) => s.userId === userId && s.poId === po.poId)
    .first();

  if (!invoice) return null; // need at least invoice + PO for a meaningful match.
  return doMatch({ po, slip, invoice });
}

/** Triggered after invoice save: try to match if PO + slip are findable. */
export async function tryMatchByInvoice(invoiceId) {
  const userId = await requireUserId();
  const invoice = await db.invoices.get(invoiceId);
  if (!invoice || invoice.userId !== userId) return null;

  // Find a PO either by explicit poNumber/poId or by recent draft/submitted for vendor.
  let po = null;
  if (invoice.poNumber || invoice.poId) {
    po = await db.purchaseOrders.where('vendorId').equals(invoice.vendorId)
      .filter((p) => p.userId === userId && (p.poId === invoice.poId || p.poId === invoice.poNumber || (p.notes || '').includes(invoice.poNumber || '')))
      .first();
  }
  if (!po) return null;

  const slip = await db.deliverySlips
    .where('vendorId').equals(invoice.vendorId)
    .filter((s) => s.userId === userId && s.poId === po.poId)
    .first();

  return doMatch({ po, slip, invoice });
}

async function doMatch({ po, slip, invoice }) {
  const userId = await requireUserId();
  const [poLines, slipLines, invLines] = await Promise.all([
    db.poLineItems.where('poId').equals(po.poId).toArray(),
    slip ? db.deliveryLineItems.where('slipId').equals(slip.slipId).toArray() : [],
    db.lineItems.where('invoiceId').equals(invoice.invoiceId).toArray(),
  ]);

  /** Build maps keyed by itemId, falling back to fuzzy description match. */
  const byItem = (rows) => {
    const m = new Map();
    for (const r of rows) {
      if (r.itemId) {
        const arr = m.get(r.itemId) || [];
        arr.push(r);
        m.set(r.itemId, arr);
      }
    }
    return m;
  };
  const poMap = byItem(poLines);
  const slipMap = byItem(slipLines);
  const invMap = byItem(invLines);

  const variances = [];

  // Iterate every itemId that appears anywhere in the triple.
  const allItemIds = new Set([...poMap.keys(), ...slipMap.keys(), ...invMap.keys()]);
  for (const itemId of allItemIds) {
    const poLine   = (poMap.get(itemId) || [])[0];
    const slipLine = (slipMap.get(itemId) || [])[0];
    const invLine  = (invMap.get(itemId) || [])[0];

    const description = invLine?.description || slipLine?.description || poLine?.description || '';
    const qtyOrdered = poLine?.quantity;
    const qtyDelivered = slipLine?.quantityDelivered;
    const qtyInvoiced = invLine?.quantity;

    // Short / over (delivered vs ordered).
    if (qtyOrdered != null && qtyDelivered != null) {
      if (qtyDelivered < qtyOrdered) {
        variances.push({ type: 'short', itemId, description, qtyOrdered, qtyDelivered, qtyInvoiced, resolution: 'open' });
      } else if (qtyDelivered > qtyOrdered) {
        variances.push({ type: 'over', itemId, description, qtyOrdered, qtyDelivered, qtyInvoiced, resolution: 'open' });
      }
    }

    // Price change (PO vs invoice).
    if (poLine?.unitPriceCents != null && invLine?.unitPriceCents != null) {
      if (Math.abs(invLine.unitPriceCents - poLine.unitPriceCents) > PRICE_CHANGE_TOLERANCE_CENTS) {
        variances.push({
          type: 'price_change',
          itemId, description,
          qtyOrdered, qtyDelivered, qtyInvoiced,
          poUnitPriceCents: poLine.unitPriceCents,
          invoiceUnitPriceCents: invLine.unitPriceCents,
          resolution: 'open',
        });
      }
    }
  }

  // Substitution detection: items on the slip that don't match any PO item by
  // description similarity get flagged.
  for (const sl of slipLines) {
    if (sl.itemId) continue; // matched by itemId already considered above.
    let bestSim = 0;
    let bestPo = null;
    for (const pl of poLines) {
      const s = similarity(sl.description, pl.description || '');
      if (s > bestSim) { bestSim = s; bestPo = pl; }
    }
    if (bestPo && bestSim < DESCRIPTION_SIMILARITY_FLOOR) {
      variances.push({
        type: 'substitution',
        itemId: bestPo.itemId,
        description: bestPo.description || '',
        substitutedDescription: sl.description,
        qtyOrdered: bestPo.quantity,
        qtyDelivered: sl.quantityDelivered,
        resolution: 'open',
      });
    }
  }

  /** @type {MatchResult} */
  const result = {
    matchedAt: Date.now(),
    poId: po.poId,
    slipId: slip?.slipId,
    invoiceId: invoice.invoiceId,
    variances,
  };
  await db.invoices.put({ ...invoice, matchResult: result });

  // Update slip status and linkage.
  if (slip) {
    await db.deliverySlips.put({
      ...slip,
      invoiceId: invoice.invoiceId,
      status: variances.length ? 'VARIANCE' : 'MATCHED',
    });
  }

  // If variances exist, surface a single alert. Dedupe by invoiceId so re-running
  // the match (e.g. after editing a line) doesn't pile up duplicate alerts.
  if (variances.length) {
    const existing = await db.alerts.where('userId').equals(userId)
      .filter((a) => a.kind === 'THREE_WAY_VARIANCE' && a.meta?.invoiceId === invoice.invoiceId)
      .first();
    const alertRow = {
      alertId: existing?.alertId || crypto.randomUUID(),
      userId,
      severity: variances.some((v) => v.type === 'short' || v.type === 'price_change') ? 'warning' : 'info',
      kind: 'THREE_WAY_VARIANCE',
      title: `${variances.length} variance${variances.length === 1 ? '' : 's'} on invoice ${invoice.invoiceNumber || ''}`,
      body: variances.map((v) => v.type).join(', '),
      createdAt: existing?.createdAt || Date.now(),
      dismissed: 0,
      meta: { invoiceId: invoice.invoiceId, poId: po.poId, slipId: slip?.slipId },
    };
    await db.alerts.put(alertRow);
  }

  return result;
}

/** Resolve a single variance — user action from the matches report. */
export async function resolveVariance(invoiceId, index, resolution) {
  const userId = await requireUserId();
  if (!['approved', 'disputed', 'adjusted'].includes(resolution)) throw new Error('resolveVariance: invalid resolution');
  const inv = await db.invoices.get(invoiceId);
  if (!inv || inv.userId !== userId || !inv.matchResult) throw new Error('resolveVariance: not found');
  const v = inv.matchResult.variances?.[index];
  if (!v) throw new Error('resolveVariance: index out of range');
  v.resolution = resolution;
  await db.invoices.put(inv);
  return v;
}

/** Invoices with at least one unresolved variance. */
export async function unresolvedReport() {
  const userId = await requireUserId();
  const rows = await db.invoices.where('userId').equals(userId).toArray();
  return rows
    .filter((i) => i.matchResult && i.matchResult.variances?.some((v) => v.resolution === 'open'))
    .sort((a, b) => b.matchResult.matchedAt - a.matchResult.matchedAt);
}
